import { useEffect } from 'react';
import { FloatingTreeType, FloatingContext, OpenChangeReason } from '@floating-ui/react';

export interface UseManualDismissProps {
  controlled?: boolean;
  rootId: string;
  tree: FloatingTreeType;
  context: FloatingContext;
  reasons?: Array<Partial<OpenChangeReason>>;
  callbackTypeWithFn?: (type: OpenChangeReason) => void;
}

function getActiveFloating(rootId: string, context: FloatingContext) {
  const floatingRootElement = document.getElementById(rootId);
  const activeNodeRef = context.elements.floating;
  const activeSubRoot = activeNodeRef?.parentNode?.parentNode as HTMLElement;

  if (floatingRootElement?.dataset?.floatingUiInert != null || activeSubRoot?.dataset?.floatingUiInert != null) {
    return false;
  }

  return true;
}

/**
 * @desc Modal dismiss event share
 *  - [Dismiss](https://floating-ui.com/docs/useDismiss)실행시 context 이벤트를 구독하여 중첩 요소간 이벤트 순차 핸들링을 지원합니다.
 *  - 제어 조건
 *    - FloatingTreeContext를 통해 생성된 요소로 useFloatingNodeId를 통한 context 주입이 되어야합니다.
 *    - 노드 구조가 자식 요소로써 중첩 호출된 케이스인 경우 자동으로 순차 호출된 dismiss 이벤트를 전달합니다.(https://github.com/floating-ui/floating-ui/pull/2114)
 *    - 노드 구조가 형제 요소로써 중첩 호출된 케이스인 경우 현재 활성된 요소부터 dismiss 이벤트를 전달합니다.
 */
export function useManualDismiss({
  controlled = true,
  rootId,
  tree,
  context,
  reasons = ['outside-press', 'escape-key'],
  callbackTypeWithFn,
}: UseManualDismissProps) {
  useEffect(() => {
    const onOpenChange = ({ reason }: { reason: OpenChangeReason }) => {
      if (!reasons.includes(reason) || !controlled) {
        return;
      }

      const nodesRef = tree.nodesRef.current;

      if (nodesRef && nodesRef.length === 1) {
        // Sibling component to the tree context
        // @ts-expect-error : TS2345
        const activeFloating = getActiveFloating(rootId, tree.nodesRef.current[0].context);
        if (activeFloating) {
          callbackTypeWithFn?.(reason);
        }
      } else {
        // Nested component to the tree context
        let activeFloating = true;
        nodesRef.forEach((child) => {
          if (child.context?.open) {
            activeFloating = getActiveFloating(rootId, child.context);
          }
        });

        if (activeFloating) {
          callbackTypeWithFn?.(reason);
        }
      }
    };
    context.events.on('openchange', onOpenChange);
    return () => {
      context.events.off('openchange', onOpenChange);
    };
  });
}
